import { FC, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, Link as RouterLink, useParams } from 'react-router';
import { Avatar, Box, Button, Chip, Divider, Grid, IconButton, LinearProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack, CreditCard, Favorite, NumbersSharp, PinDropSharp, Roofing, School } from '@mui/icons-material';
import { marked } from 'marked';
import 'photoswipe/dist/photoswipe.css';

import { useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';
import { PermissionedView } from '@cvt/components/Permissioned';
import { ListLoading } from '@cvt/components/layout/ListLoading';

import { MajorsList } from '@modules/Majors/partials/MajorsList';
import { InfiniteMajorsList } from '@modules/Majors/partials/InfiniteMajorsList';
import { MajorRow, MajorRowSkeleton } from '@modules/Majors/components/MajorRow';
import { InfiniteAssertionsList } from '@modules/Assertions/partials/InfiniteAssertionsList';
import { AnsweredAssertionRow, AnsweredAssertionRowSkeleton } from '@modules/Assertions/components/AnsweredAssertionRow';

import { formatLargeNumber, formatPercentage } from '@shared/helpers/number';
import { ViewHeader } from '@shared/components/ViewHeader';
import { VideoCoverSection } from '@shared/components/VideoCoverSection';

import { useUniversity } from '../../hooks/useUniversity';
import { useUniversityCrud } from '../../hooks/useUniversityCrud';
import { UniversityGallery } from '../../partials/UniversityGallery';
import { UniversityAdmissionsButton } from '../../components/UniversityAdmissionButton';
import { ViewUniversityAdmissionContacts } from '../../components/ViewUniversityAdmissionContacts';
import { ViewUniversityMajor as ViewUniversityMajorView } from './views/ViewUniversityMajor';

interface ViewProps extends Universities.University {
}

export const ViewUniversityView: FC<ViewProps> = university => {
  const router = useRouter();
  const location = useLocation();

  const { likeUniversity, unlikeUniversity } = useUniversityCrud();

  const actionsDisabled = [likeUniversity.isPending, unlikeUniversity.isPending].some(value => value);

  return (
    <Stack direction="column">
      <Box position="relative" width="100%" overflow="hidden" bgcolor="tertiary.light">
        <VideoCoverSection image={university.banner} video={university.video} alt={university.name} />
      </Box>
      <Routes>
        <Route
          path="/"
          element={(
            <Grid container spacing={2}>
              <Grid size={12}>
                <ViewHeader
                  category={university.sponsored && (
                    <Typography component="span" display="inline-block" mr={1} fontSize={12} color="secondary">
                      Official Partner
                    </Typography>
                  )}
                  title={university.name}
                  slots={{
                    left: (
                      <>
                        <IconButton component={RouterLink} to={router.universities.path} color="inherit">
                          <ArrowBack/>
                        </IconButton>
                        <Avatar src={university.logo} alt={university.name} />
                      </>
                    ),
                    right: (
                      <Box>
                        {university.match.isFavourite ? (
                          <IconButton onClick={() => unlikeUniversity.mutateAsync(university.id)} disabled={actionsDisabled} color="error">
                            <Favorite />
                          </IconButton>
                        ) : (
                          <IconButton onClick={() => likeUniversity.mutateAsync(university.id)} disabled={actionsDisabled}>
                            <Favorite />
                          </IconButton>
                        )}
                      </Box>
                    ),
                  }}
                />
                <Box px={2} mb={4}>
                  <Box mb={3}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} pb={1}>
                      <Typography variant="body2">{formatPercentage(university.match.value)} Match</Typography>
                      <Button component={RouterLink} to={router.universities.view(university.id).assertions.path} color="primary" size="small" variant="text">
                          See Answers
                      </Button>
                    </Stack>
                    <LinearProgress
                      value={university.match.value * 100}
                      variant="determinate"
                      sx={{
                        height: 10,
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                  <Stack direction="row" spacing={2} mb={4}>
                    {university.globalRank && (
                      <Paper
                        component={Box}
                        variant="outlined"
                        p={1}
                        mb={2}
                        width="30%"
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar variant="rounded" sx={theme => ({ width: 32, height: 32, backgroundColor: theme.palette.primary.main, color: 'common.white' })}>
                            <NumbersSharp color="inherit"/>
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" lineHeight={1} mb={0.4}>Rank</Typography>
                            <Typography variant="body1" fontWeight={600} lineHeight={1}>{university.globalRank}</Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    )}
                    {university.location && (
                      <Paper
                        component={Box}
                        variant="outlined"
                        p={1}
                        mb={2}
                        width="70%"
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar variant="rounded" sx={theme => ({ width: 32, height: 32, backgroundColor: theme.palette.secondary.main, color: 'common.white' })}>
                            <PinDropSharp color="inherit"/>
                          </Avatar>
                          <Box overflow="hidden">
                            <Typography variant="subtitle1" lineHeight={1} mb={0.4}>Location</Typography>
                            <Link href={`https://www.google.com/maps/place/${university.location.title}/`} color="textPrimary" underline="hover">  
                              <Typography variant="body2" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" lineHeight={1} fontWeight={500}>{university.location.title}</Typography>
                            </Link>
                          </Box>
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                  {university.description && (
                    <Typography variant="body1" dangerouslySetInnerHTML={{ __html: marked(university.description) }} />
                  )}
                  {university.website && (
                    <Link href={university.website} color="secondary">Discover more</Link>
                  )}
                </Box>
                {/* MAJORS OFFERED */}
                <Box
                  p={2}
                  width="100%"
                  sx={theme => ({ backgroundColor: theme.palette.background.default })}
                >
                  <Typography variant="subtitle1" mb={0.5}>Majors offered</Typography>
                  <PermissionedView permission="majors.list">
                    <Paper variant="outlined">
                      <Suspense key={location.key} fallback={(
                        <ListLoading
                          disablePadding
                          repeat={3}
                          renderItem={() => <MajorRowSkeleton/>}
                        />
                      )}>
                        <MajorsList
                          filters={{
                            university: university.id,
                            limit: 3,
                          }}
                          renderItem={major => <MajorRow key={major.id} {...major} linkTo={router.universities.view(university.id).majors.view(major.id).path} />}
                          options={{
                            enablePagination: false,
                          }}
                        />
                      </Suspense>
                      <Divider/>
                      <Button
                        fullWidth
                        component={RouterLink}
                        to={router.universities.view(university.id).majors.path}
                      >
                        View all
                      </Button>
                    </Paper>
                  </PermissionedView>
                </Box>
                {/* TUITION AND FINANCIALS */}
                {university.financials && (
                  <Box
                    p={2}
                    mb={2}
                    width="100%"
                    sx={{ backgroundColor: 'primary.dark', color: 'primary.contrastText' }}
                  >
                    <Typography variant="subtitle1" mb={0.5}>Tuition and Financials</Typography>
                    <Typography variant="body1">{university.financials}</Typography>
                    {university.studentLoanAvailable && (
                      <Paper
                        component={Box}
                        variant="outlined"
                        p={1}
                        mt={2}
                        sx={{
                          backgroundColor: 'common.white',
                          borderColor: 'primary.dark',
                          color: 'primary.dark',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar variant="rounded" sx={theme => ({ width: 32, height: 32, backgroundColor: theme.palette.primary.dark, color: 'common.white' })}>
                            <CreditCard color="inherit"/>
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" lineHeight={1} mb={0.4}>Loan</Typography>
                            <Typography variant="body2" lineHeight={1} fontWeight={500}>
                              Student loan available
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    )}
                  </Box>
                )}
                {/* CAMPUS & STUDENT LIFE */}
                <Box
                  p={2}
                  mb={2}
                  width="100%"
                >
                  <Typography variant="subtitle1" mb={0.5}>Campus & Student Life</Typography>
                  <Box
                    mt={2}
                    mb={2}
                  >
                    {university.numberOfStudents && (
                      <Box display="inline-block" mr={1} mb={1}>
                        <Chip
                          label={`${formatLargeNumber(university.numberOfStudents)} Students`}
                          color="default"
                          size="small"
                        />
                      </Box>
                    )}
                    {university.studentToFacultyRatio && (
                      <Box display="inline-block" mr={1} mb={1}>
                        <Chip
                          label={(
                            <Stack component="span" direction="row" justifyContent="center" spacing={0.5}>
                              <span>{university.studentToFacultyRatio}</span> <School fontSize="small" color="inherit" /> <span>ratio</span>
                            </Stack>
                          )}
                          color="default"
                          size="small"
                        />
                      </Box>
                    )}
                    {university.internationalStudentRatio && (
                      <Box display="inline-block" mr={1} mb={1}>
                        <Chip
                          label={(
                            <Stack component="span" direction="row" justifyContent="center" spacing={0.5}>
                              <span>{formatPercentage(university.internationalStudentRatio)} international</span>
                            </Stack>
                          )}
                          color="default"
                          size="small"
                        />
                      </Box>
                    )}
                    {university.housingAvailability && (
                      <Box display="inline-block" mr={1} mb={1}>
                        <Chip
                          icon={<Roofing color="inherit"/>}
                          label="Housing available"
                          color="default"
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                  {university.campusStudentLife && (
                    <Typography variant="body1">{university.campusStudentLife}</Typography>
                  )}
                </Box>
                {/* IMAGES */}
                {university.images && university.images?.length > 0 && (
                  <Box mb={2}>
                    <UniversityGallery
                      data={university.images.map((image) => ({
                        sourceId: image.id,
                        original: image.image,
                        thumbnail: image.thumbnail || image.image,
                        width: image.width,
                        height: image.height,
                      }))}
                    />
                  </Box>
                )}
                <UniversityAdmissionsButton university={university} />
                <ViewUniversityAdmissionContacts {...university} />
              </Grid>
            </Grid>
          )}
        />
        <Route
          path="/assertions"
          element={(
            <>
              <ViewHeader
                category="University"
                title={university.name}
                slots={{
                  left: (
                    <>
                      <IconButton component={RouterLink} to={router.universities.view(university.id).path} color="primary">
                        <ArrowBack/>
                      </IconButton>
                      <Avatar src={university.logo} alt={university.name} />
                    </>
                  ),
                  right: (
                    <Box>
                      {university.match.isFavourite ? (
                        <IconButton onClick={() => unlikeUniversity.mutateAsync(university.id)} disabled={actionsDisabled} color="primary">
                          <Favorite />
                        </IconButton>
                      ) : (
                        <IconButton onClick={() => likeUniversity.mutateAsync(university.id)} disabled={actionsDisabled}>
                          <Favorite />
                        </IconButton>
                      )}
                    </Box>
                  ),
                }}
              />
              <Box px={2}>
                <Typography variant="subtitle1" mb={0.5}>Answered assertions</Typography>
              </Box>
              <Box>
                <ErrorBoundary>
                  <PermissionedView permission="assertions.list">
                    <Suspense key={location.pathname} fallback={(
                      <ListLoading
                        repeat={3}
                        renderItem={() => <AnsweredAssertionRowSkeleton/>}
                      />
                    )}>
                      <InfiniteAssertionsList
                        filters={{
                          university: university.id,
                          answered: true,
                        }}
                        renderItem={university => <AnsweredAssertionRow key={university.id} {...university} />}
                        renderSkeleton={() => <AnsweredAssertionRowSkeleton/>}
                      />
                    </Suspense>
                  </PermissionedView>
                </ErrorBoundary>
              </Box>
            </>
          )}
        />
        <Route
          path="/majors"
          element={(
            <Grid container spacing={2}>
              <Grid size={12}>
                <ViewHeader
                  category="University"
                  title={university.name}
                  slots={{
                    left: (
                      <>
                        <IconButton component={RouterLink} to={router.universities.view(university.id).path} color="primary">
                          <ArrowBack/>
                        </IconButton>
                        <Avatar src={university.logo} alt={university.name} />
                      </>
                    ),
                    right: (
                      <Box>
                        {university.match.isFavourite ? (
                          <IconButton onClick={() => unlikeUniversity.mutateAsync(university.id)} disabled={actionsDisabled} color="primary">
                            <Favorite />
                          </IconButton>
                        ) : (
                          <IconButton onClick={() => likeUniversity.mutateAsync(university.id)} disabled={actionsDisabled}>
                            <Favorite />
                          </IconButton>
                        )}
                      </Box>
                    ),
                  }}
                />
                <Box px={2}>
                  <Typography variant="subtitle1" mb={0.5}>Majors offered</Typography>
                </Box>
                <Box>
                  <ErrorBoundary>
                    <PermissionedView permission="majors.list">
                      <Suspense key={location.pathname} fallback={(
                        <ListLoading
                          repeat={3}
                          renderItem={() => <MajorRowSkeleton/>}
                        />
                      )}>
                        <InfiniteMajorsList
                          filters={{
                            university: university.id,
                          }}
                          renderItem={major => <MajorRow key={major.id} {...major} linkTo={router.universities.view(university.id).majors.view(major.id).path} />}
                          renderSkeleton={() => <MajorRowSkeleton/>}
                        />
                      </Suspense>
                    </PermissionedView>
                  </ErrorBoundary>
                </Box>
              </Grid>
            </Grid>
          )}
        />
        <Route path="*" element={<Navigate to={router.universities.view(university.id).path} replace />}/>
      </Routes>
    </Stack>
  );
};

export const ViewUniversityMajor: FC<Universities.University> = university => {
  const { majorId = '' } = useParams();

  return (
    <ViewUniversityMajorView
      id={parseInt(majorId, 10)}
      university={university}
    />
  );
};

interface Props {
  id: number;
}

export const ViewUniversity: FC<Props> = ({ id }) => {

  const router = useRouter();

  const { university } = useUniversity({
    id,
  });

  if (!university) {
    return null;
  }

  return (
    
    <Routes>
      <Route path="/majors/:majorId/*" element={<ViewUniversityMajor {...university} />} />
      <Route path="/*" element={<ViewUniversityView {...university} />}/>
      <Route path="*" element={<Navigate to={router.universities.view(university.id).path} replace />}/>
    </Routes>
  );
};

